import { StateCreator } from 'zustand';
import { HighwaysDetailsSlice } from './highwaysDetails';
import { YourDetailsSlice } from './yourDetails';
import {
  CaseDetailsObjectType,
  FileDetailsObjectType,
  HighwaysDetails,
  HighwaysServiceDetailsObjectType,
  PrimaryConsumerDetailsObjectType,
  ServiceDeliveryLocationDetailsObjectType,
  CoreServiceDetailsObjectType,
  ServiceRegistrationDetailsObjectType,
} from '@highways/api/type';
import {
  formatIssueDetails,
  formatIssueType,
} from '@highways/utils/formatDataForSubmission';

export type CombinedSlice = {
  getDataForSubmission: () => HighwaysDetails;
};

export const createCombinedSlice: StateCreator<
  HighwaysDetailsSlice & YourDetailsSlice,
  [],
  [],
  CombinedSlice
> = (set, get) => ({
  getDataForSubmission: () => {
    const {
      firstName,
      lastName,
      email,
      sessionId,
      wantConfirmationEmail,
      highwaysType,
      damageType,
      hasDamageCausedInjury,
      anythingElseText,
      serviceAssetDetails,
      serviceLocationDetails,
      fileDetailsObjects,
      uploadedImagesBlob,
    } = get();
    const FileDetailsObject: FileDetailsObjectType | undefined =
      fileDetailsObjects.length > 0
        ? {
            external: false,
            FileDetailObject: fileDetailsObjects.map((file) => {
              if (!file.path) {
                return {
                  ...file,
                  path: uploadedImagesBlob.filter(
                    (item) => item.filename === file.name,
                  )[0].blob_uri,
                };
              }
              return file;
            }),
          }
        : undefined;

    const HighwaysServiceDetailsObject: HighwaysServiceDetailsObjectType = {
      issue_type: formatIssueType(highwaysType),
      issue_details: formatIssueDetails(damageType),
    };

    const CaseDetailsObject: CaseDetailsObjectType = {
      ReceiptDetailsObject: {
        channel: 'Online',
        platform: 'React',
        submission_date: new Date().toISOString(),
      },
      TypeDetailsObject: {
        code: 'HWRDMG',
      },
    };

    const PrimaryConsumerDetailsObject:
      | PrimaryConsumerDetailsObjectType
      | undefined =
      wantConfirmationEmail === 'Yes'
        ? {
            ContactEmailDetailsObject: [
              {
                email_address: email,
                type: 'H',
              },
            ],
            NameDetailsObject: {
              first_name: firstName,
              last_name: lastName,
              middle_name: '',
            },
          }
        : undefined;

    // API schema does not accept full_address in ServiceLocationDetailsObject
    const { full_address, ...serviceLocationDetailsExFullAddress } =
      serviceLocationDetails;
    const ServiceDeliveryLocationDetailsObject: ServiceDeliveryLocationDetailsObjectType =
      {
        ServiceAssetDetailsObject: serviceAssetDetails,
        ServiceLocationDetailsObject: serviceLocationDetailsExFullAddress,
      };

    const CoreServiceDetailsObject: CoreServiceDetailsObjectType = {
      dangerous: hasDamageCausedInjury === 'Yes',
      IncidentDescriptionObject: {
        description1: anythingElseText,
      },
    };

    const ServiceRegistrationDetailsObject: ServiceRegistrationDetailsObjectType =
      {
        consumer_contact_primary: wantConfirmationEmail === 'Yes',
      };

    const SessionDetailsObject = {
      session_id: sessionId,
    };

    return {
      FileDetailsObject,
      HighwaysServiceDetailsObject,
      CaseDetailsObject,
      PrimaryConsumerDetailsObject,
      ServiceDeliveryLocationDetailsObject,
      CoreServiceDetailsObject,
      ServiceRegistrationDetailsObject,
      SessionDetailsObject,
    };
  },
});
